import React, { useRef, useReducer, useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import PageList from '../../templates/PageList';
import PageSearch from '../../molecules/PageSearch';
import SelectFilter from '../../organisms/SelectFilter';
import CreditOperationModalForm from './CreditOperationModalForm';
import { useFileMapping } from '../../../hooks/useFileMapping';

const DROPBOX_PATHS: Record<string, string> = {
    'CPR-F ASSINADAS APROVADAS': 'PLANEJAMENTO E CONTROLE/JURIDICO/CPR-F/CPR-F ASSINADAS APROVADAS',
    'CPRF_PJ-PF_ESTRUTURADAS': 'PLANEJAMENTO E CONTROLE/JURIDICO/CPR-F/CPRF_PJ-PF_ESTRUTURADAS',
    'BATIMENTO DIMENSA': 'PLANEJAMENTO E CONTROLE/JURIDICO/CPR-F/BATIMENTO DIMENSA',
    '03_ADITAMENTOS E SUB-ROGAÇÕES': 'PLANEJAMENTO E CONTROLE/JURIDICO/CONTENCIOSO/OPERAÇÕES/03_ADITAMENTOS E SUB-ROGAÇÕES',
};

const buildDropboxLink = (rootFolder: string, process: string, pdfFile: string) => {
    const base = DROPBOX_PATHS[rootFolder] || rootFolder;
    const path = process === '[RAIZ]' ? `${base}/${pdfFile}` : `${base}/${process}/${pdfFile}`;
    return `dropbox://open?path=${encodeURIComponent('/' + path)}`;
};

const FilePageList: React.FC = () => {
    const {
        page, pageSize, fieldSorted, orderSorted, searchTerm,
        extractableFilter, syncedFilter, linkedFilter,
        url, API_KEY,
        pageChanged, pageSizeChanged, pageSorted,
        searchChanged, extractableChanged, syncedChanged, linkedChanged, onFilterClean,
    } = useFileMapping();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [, forceRender] = useReducer(x => x + 1, 0);
    const overrides = useRef<Record<number, { contract_number: string; operation_id: string }>>({});

    const openModal = (row: any) => {
        setSelectedRow(row);
        setModalOpen(true);
    };

    const handleSaved = (fileId: number, contractNumber: string, operationId: string) => {
        const id = Number(fileId);
        overrides.current[id] = { contract_number: contractNumber, operation_id: operationId };
        for (const [key, val] of Object.entries(overrides.current)) {
            if (Number(key) !== id && val.contract_number === contractNumber) {
                overrides.current[Number(key)] = { contract_number: '', operation_id: '' };
            }
        }
        forceRender();
    };

    const renderRows = (rows: any[]) => (
        <>
            {rows.map((row: any, index: number) => {
                const o = overrides.current[row.id];
                const contractNumber = o?.contract_number ?? row.contract_number;
                const operationId = o?.operation_id ?? row.operation_id;
                const displayRow = { ...row, contract_number: contractNumber, operation_id: operationId };
                return (
                    <tr key={row.id} className="group hover:bg-gray-50 cursor-pointer" onClick={() => openModal(displayRow)}>
                        <td className="px-3 py-1 text-xs truncate max-w-[280px]" title={row.root_folder}>{row.root_folder}</td>
                        <td className="px-3 py-1 text-xs truncate max-w-[200px]" title={row.process}>{row.process}</td>
                        <td className="px-3 py-1 text-xs font-medium">{row.pdf_file}</td>
                        <td className="px-3 py-1 text-xs text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${row.extractable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {row.extractable ? 'Sim' : 'Não'}
                            </span>
                        </td>
                        <td className="px-3 py-1 text-xs text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${row.synced ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-600'}`}>
                                {row.synced ? 'Sim' : 'Não'}
                            </span>
                        </td>
                        <td className="px-3 py-1 text-xs text-gray-700">{contractNumber || '-'}</td>
                        <td className="px-3 py-1 text-center" onClick={e => e.stopPropagation()}>
                            <a
                                href={buildDropboxLink(row.root_folder, row.process, row.pdf_file)}
                                title="Abrir no Dropbox"
                                className="invisible group-hover:visible text-blue-500 hover:text-blue-700"
                            >
                                <FiExternalLink size={14} />
                            </a>
                        </td>
                    </tr>
                );
            })}
        </>
    );

    return (
        <>
            <PageList
                pageTitle="Catálogo de Arquivos Jurídicos"
                url={url}
                fetchHeaders={[
                    { key: 'x-api-key', value: API_KEY ?? '' },
                    { key: 'Authorization', value: `Bearer ${localStorage.getItem('authToken')}` },
                ]}
                breadcrumb={[{ label: 'Jurídico', url: '#' }, { label: 'Catálogo de Arquivos', url: '#' }]}
                tableHeaders={[
                    { id: 'col-root-folder', field: 'root_folder', title: 'Pasta Raiz', canSort: true, width: '280px' },
                    { id: 'col-process', field: 'process', title: 'Processo', canSort: true, width: '400px' },
                    { id: 'col-pdf-file', field: 'pdf_file', title: 'Arquivo PDF', canSort: true },
                    { id: 'col-extractable', field: 'extractable', title: 'Extraível', canSort: false, width: '50px' },
                    { id: 'col-synced', field: 'synced', title: 'Sinc.', canSort: false, width: '50px' },
                    { id: 'col-contract', field: 'contract_number', title: 'N. Contrato', canSort: true, width: '80px' },
                    { id: 'col-action', field: '', title: '', canSort: false, width: '40px' },
                ]}
                tableFieldSort={fieldSorted as string}
                tableOrderSort={orderSorted as 'ASC' | 'DESC'}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={Number(page)}
                pageSize={Number(pageSize)}
                onPageChange={pageChanged}
                onItemsPerPageChange={pageSizeChanged}
                defaultBackendHeader={false}
                itemsLabel="arquivos"
            >
                {{
                    filterSlot: (
                        <div className="flex gap-1 items-end">
                            <SelectFilter
                                id="extractable-filter"
                                label="Extraível"
                                value={extractableFilter}
                                onChange={extractableChanged}
                                defaultVoidOption={true}
                                defaultLabel="Todos"
                                options={[
                                    { value: 'true', label: 'Sim' },
                                    { value: 'false', label: 'Não' },
                                ]}
                                className="w-32"
                            />
                            <SelectFilter
                                id="synced-filter"
                                label="Sincronizado"
                                value={syncedFilter}
                                onChange={syncedChanged}
                                defaultVoidOption={true}
                                defaultLabel="Todos"
                                options={[
                                    { value: 'true', label: 'Sim' },
                                    { value: 'false', label: 'Não' },
                                ]}
                                className="w-36"
                            />
                            <SelectFilter
                                id="linked-filter"
                                label="Vinculado"
                                value={linkedFilter}
                                onChange={linkedChanged}
                                defaultVoidOption={true}
                                defaultLabel="Todos"
                                options={[
                                    { value: 'true', label: 'Sim' },
                                    { value: 'false', label: 'Não' },
                                ]}
                                className="w-32"
                            />
                            <PageSearch
                                value={searchTerm}
                                label="Buscar por arquivo, processo, pasta..."
                                onSearch={searchChanged}
                                filterCleanVisible={true}
                                filterCleanEnabled={Boolean(searchTerm) || Boolean(extractableFilter) || Boolean(syncedFilter) || Boolean(linkedFilter)}
                                onFilterClean={onFilterClean}
                                configureVisible={false}
                                className="flex-1"
                            />
                        </div>
                    ),
                    controlSlot: null,
                }}
            </PageList>

            <CreditOperationModalForm
                show={modalOpen}
                row={selectedRow}
                onClose={() => setModalOpen(false)}
                onSaved={handleSaved}
            />
        </>
    );
};

export default FilePageList;
